import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, RotateCcw, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { subscribeJobs, dismissJob, startLinkJob } from "@/lib/linkJobs";

const JobResult = ({ job }) => {
  const r = job.result;
  const [open, setOpen] = useState(false);
  const retry = async () => {
    try {
      await startLinkJob(r.failed.map((f) => f.item), job.kind, `Retry: ${job.title}`, job.label);
      dismissJob(job.id);
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <div className="space-y-2 text-sm">
      <p data-testid={`link-job-result-${job.id}`}>
        <b>{r.updated}</b> client{r.updated === 1 ? "" : "s"} updated
        {r.skipped?.length ? <>, <b>{r.skipped.length}</b> skipped (already set or no longer linked)</> : null}
        {r.failed?.length ? <>, <b className="text-red-600">{r.failed.length} not saved</b></> : null}
      </p>
      {r.failed?.length > 0 && (
        <>
          <button className="text-xs underline" onClick={() => setOpen((v) => !v)} data-testid={`link-job-failed-toggle-${job.id}`}>{open ? "Hide" : "Show"} rejected clients</button>
          {open && (
            <ul className="max-h-40 overflow-y-auto text-xs space-y-1" data-testid={`link-job-failed-list-${job.id}`}>
              {r.failed.map((f, i) => <li key={i}><b>{f.name}</b> · {f.role_label}: {f.reason}</li>)}
            </ul>
          )}
          <p className="text-xs text-slate-500">Rejected clients were left exactly as they were in the portal and the CRM.</p>
          <Button size="sm" variant="outline" onClick={retry} data-testid={`link-job-retry-${job.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Retry failed only</Button>
        </>
      )}
    </div>
  );
};

export const LinkJobCenter = () => {
  const [jobs, setJobs] = useState([]);
  useEffect(() => subscribeJobs(setJobs), []);
  if (!jobs.length) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[60] w-96 space-y-3" data-testid="link-job-center">
      {jobs.map((j) => {
        const done = j.status === "done";
        const bad = done && j.result?.failed?.length;
        return (
          <div key={j.id} className="rounded-xl border bg-white shadow-xl p-4 space-y-2" data-testid={`link-job-${j.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 font-medium text-sm">
                {!done ? <Loader2 className="h-4 w-4 animate-spin text-[#357c2a]" /> : bad ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                {j.title}
              </div>
              {done && <button onClick={() => dismissJob(j.id)} aria-label="Close" data-testid={`link-job-close-${j.id}`}><X className="h-4 w-4 text-slate-400" /></button>}
            </div>
            {!done ? (
              <>
                <Progress value={(100 * (j.done || 0)) / Math.max(j.total, 1)} data-testid={`link-job-progress-${j.id}`} />
                <p className="text-xs text-slate-500">{j.done || 0} of {j.total} clients done — you can close panels, it keeps going</p>
              </>
            ) : <JobResult job={j} />}
          </div>
        );
      })}
    </div>
  );
};
